import React, { useEffect, useState, useRef } from 'react';
import styled from 'styled-components';
import validator from 'validator';
import { useImmer } from 'use-immer';
import { Typography } from '../typography';
import { Button } from '../button';
import { FileInput, Input, InputNumber, InputPrice } from '../input';
import {
  ModalBase,
  ModalWrapper,
  ModalFooterButtons,
  ModalBodyCustomColumns,
} from './ModalComponents';
import { enqueueSnackbar } from '../snackbar';
import {
  useContainerRepairsQuery,
  useConvertCurrencyFcQuery,
  useSetPriceRepairMutation,
  useStoragePricingCurrencyCodeQuery,
  useUpdateImagesRepairMutation,
  useUpdateRepairMutation,
} from '../../hooks';
import { CURRENCIES, currencyOptions, INPUT_ERRORS, MESSAGES } from '../../const';
import { CurrencyCode, ImageObjType } from '../../types';
import { Select } from '../select';
import { ModalImages } from './UploadImagesModal';
import { fullScreenButtonClick, HideInput, ImageGallery, ImageGalleryCard } from '../image-gallery';
import { filesFormatValidator } from '../../utils';
import { BorderLine } from '../border-line';
import { i18n } from '../../i18n';
import { SearchableSelect } from '../select';
import { InputContainer } from '../input';
import { containersService } from '../../services';
import { useStoreUser } from '../../context';

const FullWidth = styled.div`
  display: grid;
  grid-column: 1 / 4;
`;

interface RepairSetUpdateModalType {
  isSubmitClicked: boolean;
  submitClick: () => void;
  open: () => void;
  name: string;
  action: 'set-price' | 'edit' | 'repair';

  number: string;
  containerId: string;
  showContainerSelect: boolean;
  data: {
    id: string;
    price: string;
    currencyCode?: CurrencyCode;
    expectedDurationDays: number;
    description: string;
    beforeImages: { url: string; name: string }[];
  }[];
}

export const repairSetUpdateMultipleModal: RepairSetUpdateModalType = {
  isSubmitClicked: false,
  submitClick: () => null,
  open: () => null,
  name: '',
  action: 'set-price',

  number: '',
  containerId: '',
  showContainerSelect: false,
  data: [],
};

export const RepairSetUpdateMultipleModal = () => {
  const { user } = useStoreUser();
  const [open, setOpen] = useState(false);
  const [modalContainerId, setModalContainerId] = useState('');
  const [modalContainerNumber, setModalContainerNumber] = useState('');

  const showContainerSelect = repairSetUpdateMultipleModal.showContainerSelect;

  const effectiveContainerId = showContainerSelect
    ? modalContainerId
    : repairSetUpdateMultipleModal.containerId;

  const oldRepairs =
    repairSetUpdateMultipleModal.action === 'edit'
      ? []
      : useContainerRepairsQuery(effectiveContainerId, open && !!effectiveContainerId);

  const setPriceMutation = useSetPriceRepairMutation();
  const updateMutation = useUpdateRepairMutation();
  const updateImagesRepairMutation = useUpdateImagesRepairMutation();
  const convertCurrency = useConvertCurrencyFcQuery();
  const { data: defaultCurrencyCode } = useStoragePricingCurrencyCodeQuery(
    repairSetUpdateMultipleModal.data[0]?.currencyCode
      ? ''
      : repairSetUpdateMultipleModal.data[0]?.id,
    (currencyCode) => {
      if (repairSetUpdateMultipleModal.data[0]?.currencyCode) return;
      setCurrencyCode([currencyCode]);
    },
  );

  const [price, setPrice] = useState(['0']);
  const [currencyCode, setCurrencyCode] = useState<CurrencyCode[]>(['USD']);
  const [expectedDurationDays, setExpectedDurationDays] = useState([0]);
  const [description, setDescription] = useState(['']);
  const [zoomImageIndex, setZoomImageIndex] = useState([0, 0]);

  const [oldBeforeImages, setOldBeforeImages] = useImmer<
    RepairSetUpdateModalType['data'][number]['beforeImages'][]
  >([[]]);
  const deleteOldBeforeImages = useRef<any[][]>([[]]);
  const [newBeforeImages, setNewBeforeImages] = useImmer<any[][]>([[]]);
  const [newBeforeImagesFiles, setNewBeforeImagesFiles] = useImmer<any[][]>([[]]);
  const beforeImagesInputRef = useRef<any>([]);

  const [updateKey, setUpdateKey] = useState<any>();

  const validators = price.map((_, i) => ({
    price: !String(price[i]).length
      ? INPUT_ERRORS.REQUIRED.PRICE
      : Number(price[i]) <= 0
      ? INPUT_ERRORS.NOT_VALID.PRICE_MIN_VALUE
      : '',
    expectedDurationDays: !String(expectedDurationDays[i]).length
      ? INPUT_ERRORS.REQUIRED.DAYS_TO_COMPLETE
      : Number(expectedDurationDays[i]) <= 0
      ? INPUT_ERRORS.NOT_VALID.DAYS_TO_COMPLETE_MIN_VALUE
      : '',
    description: !description[i]
      ? INPUT_ERRORS.REQUIRED.DESCRIPTION
      : !validator.isLength(description[i], { min: 10, max: 500 })
      ? INPUT_ERRORS.NOT_VALID.REPAIR_DESCRIPTION_LENGTH
      : '',
    beforeImages: ![...oldBeforeImages[i], ...newBeforeImages[i]].length
      ? INPUT_ERRORS.REQUIRED.IMAGES
      : '',
  }));

  useEffect(() => {
    repairSetUpdateMultipleModal.open = async () => {
      const names = {
        'set-price': i18n.t('pages.modal.repairAdminApprovedTitle'),
        edit: i18n.t('pages.modal.repairUpdateTitle'),
        repair: i18n.t('pages.modal.repairTitle'),
      };
      repairSetUpdateMultipleModal.name = names[repairSetUpdateMultipleModal.action];
      if (!repairSetUpdateMultipleModal.showContainerSelect) {
        repairSetUpdateMultipleModal.name = `${repairSetUpdateMultipleModal.name}: ${repairSetUpdateMultipleModal.number}`;
      }

      if (repairSetUpdateMultipleModal.showContainerSelect) {
        setModalContainerId('');
        setModalContainerNumber('');
      }

      if (repairSetUpdateMultipleModal.data[0].currencyCode) {
        setCurrencyCode([repairSetUpdateMultipleModal.data[0].currencyCode]);
      }

      setPrice([repairSetUpdateMultipleModal.data[0].price]);
      setExpectedDurationDays([repairSetUpdateMultipleModal.data[0].expectedDurationDays]);
      setDescription([repairSetUpdateMultipleModal.data[0].description]);
      setOldBeforeImages([repairSetUpdateMultipleModal.data[0].beforeImages]);

      setNewBeforeImages([[]]);
      setNewBeforeImagesFiles([[]]);

      repairSetUpdateMultipleModal.isSubmitClicked = false;
      setOpen(true);
    };
  }, []);

  const handleCurrencyChange = (e: any, i: number) => {
    const oldCurrencyCode = currencyCode[i];
    const newCurrencyCode = e.target.value;

    const convertedValue = convertCurrency(oldCurrencyCode, newCurrencyCode, Number(price));

    price[i] = convertedValue.toFixed(2);
    setPrice(price);

    currencyCode[i] = newCurrencyCode;
    setCurrencyCode(currencyCode);
    setUpdateKey(Math.random());
  };

  const handleDeleteImageClick = (i: number, n: number) => {
    const isDeleteNewImage = n - oldBeforeImages[i].length >= 0;

    if (isDeleteNewImage) {
      n = n - oldBeforeImages[i].length;

      setNewBeforeImages((props) => {
        props[i] = props[i].filter((_: any, index: number) => index !== n);
      });
      setNewBeforeImagesFiles((props) => {
        props[i] = props[i].filter((_: any, index: number) => index !== n);
      });
    } else {
      deleteOldBeforeImages.current[i] = [
        ...deleteOldBeforeImages.current[i],
        { action: 'delete', name: oldBeforeImages[i][n].name },
      ];

      setOldBeforeImages((props) => {
        props[i] = props[i].filter((_: any, index: number) => index !== n);
      });
    }
  };

  const handleZoomClick = (i: number, n: number) => {
    setZoomImageIndex([i, n]);
    fullScreenButtonClick('repair-set-update-multiple-modal');
  };

  const handleChangeFiles = (newImages: any, newFiles: any, i: number) => {
    if (filesFormatValidator(newFiles, 'Image')) {
      enqueueSnackbar(filesFormatValidator(newFiles, 'Image'), 'warning');
      return;
    }

    setNewBeforeImages((props) => {
      props[i] = [...props[i], ...newImages];
    });

    setNewBeforeImagesFiles((props) => {
      props[i] = [...props[i], ...newFiles];
    });

    beforeImagesInputRef.current[i].type = 'text';
    beforeImagesInputRef.current[i].type = 'file';
  };

  const handleAddRepairClick = () => {
    setPrice([...price, '0']);
    setCurrencyCode([...currencyCode, defaultCurrencyCode as CurrencyCode]);
    setExpectedDurationDays([...expectedDurationDays, 0]);
    setDescription([...description, '']);

    setOldBeforeImages([...oldBeforeImages, []]);
    deleteOldBeforeImages.current = [...deleteOldBeforeImages.current, []];
    setNewBeforeImages([...newBeforeImages, []]);
    setNewBeforeImagesFiles([...newBeforeImagesFiles, []]);
  };

  const handleContainerSelect = (option: any) => {
    if (option.isEmpty) {
      setModalContainerId('');
      setModalContainerNumber('');
      repairSetUpdateMultipleModal.containerId = '';
      repairSetUpdateMultipleModal.number = '';
      repairSetUpdateMultipleModal.data[0].id = '';
      const names = {
        'set-price': i18n.t('pages.modal.repairAdminApprovedTitle'),
        edit: i18n.t('pages.modal.repairUpdateTitle'),
        repair: i18n.t('pages.modal.repairTitle'),
      };
      repairSetUpdateMultipleModal.name = names[repairSetUpdateMultipleModal.action];
      return;
    }

    setModalContainerId(option.id);
    setModalContainerNumber(option.label);
    repairSetUpdateMultipleModal.containerId = option.id;
    repairSetUpdateMultipleModal.number = option.label;
    repairSetUpdateMultipleModal.data[0].id = option.id;
    const names = {
      'set-price': i18n.t('pages.modal.repairAdminApprovedTitle'),
      edit: i18n.t('pages.modal.repairUpdateTitle'),
      repair: i18n.t('pages.modal.repairTitle'),
    };
    repairSetUpdateMultipleModal.name = `${names[repairSetUpdateMultipleModal.action]}: ${
      option.label
    }`;

    setPrice(['0']);
    setExpectedDurationDays([0]);
    setDescription(['']);
    setOldBeforeImages([[]]);
    deleteOldBeforeImages.current = [[]];
    setNewBeforeImages([[]]);
    setNewBeforeImagesFiles([[]]);
  };

  const handleRemoveLastRepairClick = () => {
    setPrice((prev) => prev.slice(0, -1));
    setCurrencyCode((prev) => prev.slice(0, -1));
    setExpectedDurationDays((prev) => prev.slice(0, -1));
    setDescription((prev) => prev.slice(0, -1));

    setOldBeforeImages((prev) => prev.slice(0, -1));
    deleteOldBeforeImages.current = deleteOldBeforeImages.current.slice(0, -1);
    setNewBeforeImages((prev) => prev.slice(0, -1));
    setNewBeforeImagesFiles((prev) => prev.slice(0, -1));
  };

  const handleOnClose = () => {
    repairSetUpdateMultipleModal.data = [];
    repairSetUpdateMultipleModal.showContainerSelect = false;
    setModalContainerId('');
    setModalContainerNumber('');
    setOpen(!open);
  };

  const handleSubmitClick = async () => {
    repairSetUpdateMultipleModal.isSubmitClicked = true;
    setUpdateKey(Math.random());

    if (newBeforeImages.some((item) => item.length > 10)) {
      enqueueSnackbar(INPUT_ERRORS.NOT_VALID.MAX_UPLOAD_FILES, 'warning');
      return;
    }

    if (showContainerSelect && !effectiveContainerId) {
      enqueueSnackbar(INPUT_ERRORS.REQUIRED.CONTAINER_NUMBER, 'warning');
      return;
    }

    if (validators.some((v) => Object.values(v).some((field) => !!field))) {
      return;
    }

    setOpen(false);

    const beforeImagesData = price.map((_, n) =>
      newBeforeImages[n].map((_: any, i: any) => ({
        action: 'upload' as ImageObjType['action'],
        file: newBeforeImagesFiles[n][i],
      })),
    );

    const updateImageMutate = (repairIds: string[]) => {
      repairIds.forEach((id, i) =>
        updateImagesRepairMutation.mutate(
          {
            id,
            photos: [...beforeImagesData[i], ...deleteOldBeforeImages.current[i]],
            action: 'before',
          },
          {
            onSuccess: () => {
              repairSetUpdateMultipleModal.submitClick();
            },
          },
        ),
      );
    };

    const mutateData = price.map((_, i: number) => ({
      id: repairSetUpdateMultipleModal.data[0].id,
      price: Number(price[i]),
      currencyCode: currencyCode[i],
      expectedDurationDays: expectedDurationDays[i],
      description: description[i],
    }));

    const mutate: any =
      repairSetUpdateMultipleModal.action === 'edit'
        ? updateMutation.mutate
        : setPriceMutation.mutate;

    mutate(repairSetUpdateMultipleModal.action === 'edit' ? mutateData[0] : mutateData, {
      onSuccess: (res: any) =>
        updateImageMutate(
          repairSetUpdateMultipleModal.action === 'edit'
            ? [repairSetUpdateMultipleModal.data[0].id]
            : res.data.ids,
        ),
      onError: () => {
        enqueueSnackbar(MESSAGES.REPAIR.ERROR.NOT_SET, 'error');
      },
    });
  };

  return (
    <>
      <ModalBase
        id={'repair-set-update-multiple-modal'}
        open={open}
        onClose={handleOnClose}
        paperSize={'very-big'}
      >
        <ModalWrapper>
          <Typography variant={'textL'} weight={'bold'}>
            {repairSetUpdateMultipleModal.name}
          </Typography>
          {showContainerSelect && (
            <SearchableSelect
              InputComponent={InputContainer}
              label={i18n.t('label.containerNumber')}
              selectedOptionLabel={
                modalContainerNumber || i18n.t('placeholder.selectContainerNumber')
              }
              onSelect={handleContainerSelect}
              queryKey={['container-short', 'repairs-add-repair', modalContainerId]}
              queryFn={() =>
                containersService.options(
                  {
                    hideAwaitingPayment: true,
                    status: 'GATED_IN',
                    short: true,
                    withoutClientYardFilter: true,
                  },
                  user,
                )
              }
              selectedAreasEqaul={{ key: 'id', keys: [modalContainerId] }}
              error={repairSetUpdateMultipleModal.isSubmitClicked && !modalContainerId}
              helperText={
                repairSetUpdateMultipleModal.isSubmitClicked && !modalContainerId
                  ? INPUT_ERRORS.REQUIRED.CONTAINER_NUMBER
                  : ''
              }
            />
          )}
          {showContainerSelect && !effectiveContainerId && (
            <ModalFooterButtons>
              <Button size={'medium'} data-color={'white'} onClick={handleOnClose}>
                {i18n.t('button.cancel')}
              </Button>
            </ModalFooterButtons>
          )}
          {(!showContainerSelect || effectiveContainerId) && (
            <>
              <ModalBodyCustomColumns columns={'35rem 3fr'} isSticky>
                <Typography variant={'textS'}>{i18n.t('label.beforeImages')}*</Typography>
                <ModalBodyCustomColumns columns={'1fr 1fr 1fr'}>
                  <Typography variant={'textS'}>{i18n.t('label.price')}*</Typography>
                  <Typography variant={'textS'}>{i18n.t('label.currencyCode')}</Typography>
                  <Typography variant={'textS'}>{i18n.t('label.daysToComplete')}*</Typography>
                </ModalBodyCustomColumns>
              </ModalBodyCustomColumns>
              {oldRepairs.map((repair, i: number) => (
                <React.Fragment key={`${i}-${updateKey}`}>
                  {i !== 0 && <BorderLine space={'none'} />}
                  <Typography variant={'textL'} weight={'bold'}>
                    {i18n.t('pages.modal.repairTitle')}:{' '}
                    {i18n.t('label.chip.' + repair.status.toLowerCase()).toUpperCase()}
                  </Typography>
                  <ModalBodyCustomColumns columns={'35rem 3fr'}>
                    <ModalBodyCustomColumns columns={'1fr'}>
                      <ModalImages isVisible={!!repair.beforeImages.length}>
                        {repair.beforeImages.map((img, n) => (
                          <ImageGalleryCard
                            key={`${i}-${n}`}
                            img={typeof img === 'string' ? img : img.url}
                            handleZoomClick={() => handleZoomClick(i, n)}
                            handleDeleteImageClick={() => null}
                            hideDeleteButton
                          />
                        ))}
                      </ModalImages>
                    </ModalBodyCustomColumns>
                    <ModalBodyCustomColumns columns={'1fr 1fr 1fr'}>
                      <InputPrice
                        placeholder={i18n.t('label.price')}
                        data-size={'medium'}
                        defaultValue={repair.price}
                        endIcon={CURRENCIES[repair.currencyCode].symbol}
                        disabled
                      />
                      <Select
                        placeholder={i18n.t('placeholder.selectCurrencyCode')}
                        options={currencyOptions}
                        value={repair.currencyCode}
                        disabled
                      />
                      <InputNumber
                        placeholder={i18n.t('placeholder.days')}
                        data-size={'medium'}
                        defaultValue={repair.expectedDurationDays}
                        disabled
                      />
                      <FullWidth>
                        <Input
                          placeholder={i18n.t('label.description')}
                          data-size={'medium'}
                          defaultValue={repair.description}
                          rows={2}
                          multiline
                          disabled
                        />
                      </FullWidth>
                    </ModalBodyCustomColumns>
                  </ModalBodyCustomColumns>
                </React.Fragment>
              ))}
              {price.map((_, i: number) => (
                <React.Fragment key={`${i}-${updateKey}`}>
                  {i + oldRepairs.length !== 0 && <BorderLine space={'none'} />}
                  <Typography variant={'textL'} weight={'bold'}>
                    {i18n.t('pages.modal.repairTitle')}-{i + 1}
                  </Typography>
                  <ModalBodyCustomColumns columns={'35rem 3fr'}>
                    <ModalBodyCustomColumns columns={'1fr'}>
                      <ModalImages
                        isVisible={!![...oldBeforeImages[i], ...newBeforeImages[i]].length}
                      >
                        {[...oldBeforeImages[i], ...newBeforeImages[i]].map((img, n) => (
                          <ImageGalleryCard
                            key={`${i}-${n}`}
                            img={typeof img === 'string' ? img : img.url}
                            handleZoomClick={() => handleZoomClick(i + oldRepairs.length, n)}
                            handleDeleteImageClick={() => handleDeleteImageClick(i, n)}
                          />
                        ))}
                        <HideInput>
                          <FileInput
                            id={'file-input'}
                            label={i18n.t('label.beforeImages')}
                            inputRef={(ref) => {
                              if (ref) {
                                beforeImagesInputRef.current[i] = ref;
                              }
                            }}
                            inputProps={{
                              accept: '.jpeg, .jpg, .png, .webp',
                              multiple: true,
                            }}
                            handleChangeFiles={(newImages, newFiles) =>
                              handleChangeFiles(newImages, newFiles, i)
                            }
                          />
                        </HideInput>
                      </ModalImages>
                      {repairSetUpdateMultipleModal.isSubmitClicked &&
                        validators[i].beforeImages && (
                          <Typography variant={'textM'} color={'--red-100'}>
                            {validators[i].beforeImages}
                          </Typography>
                        )}
                      <Button
                        size={'medium'}
                        data-color={'primary'}
                        onClick={() => beforeImagesInputRef.current[i].click()}
                        justify={'left'}
                      >
                        {i18n.t('button.addBeforeImages')}
                      </Button>
                    </ModalBodyCustomColumns>
                    <ModalBodyCustomColumns columns={'1fr 1fr 1fr'}>
                      <InputPrice
                        placeholder={i18n.t('label.price')}
                        data-size={'medium'}
                        defaultValue={price[i]}
                        onChange={(e) => {
                          const newPrice = [...price];
                          newPrice[i] = e.target.value;
                          setPrice(newPrice);
                        }}
                        endIcon={currencyCode[i] ? CURRENCIES[currencyCode[i]].symbol : ''}
                        error={
                          repairSetUpdateMultipleModal.isSubmitClicked && !!validators[i].price
                        }
                        helperText={
                          repairSetUpdateMultipleModal.isSubmitClicked ? validators[i].price : ''
                        }
                      />
                      <Select
                        placeholder={i18n.t('placeholder.selectCurrencyCode')}
                        options={currencyOptions}
                        value={currencyCode[i]}
                        onChange={(e) => handleCurrencyChange(e, i)}
                      />
                      <InputNumber
                        placeholder={i18n.t('placeholder.days')}
                        data-size={'medium'}
                        defaultValue={expectedDurationDays[i]}
                        onChange={(e) => {
                          const newDays = [...expectedDurationDays];
                          newDays[i] = Number(e.target.value);
                          setExpectedDurationDays(newDays);
                        }}
                        error={
                          repairSetUpdateMultipleModal.isSubmitClicked &&
                          !!validators[i].expectedDurationDays
                        }
                        helperText={
                          repairSetUpdateMultipleModal.isSubmitClicked
                            ? validators[i].expectedDurationDays
                            : ''
                        }
                      />
                      <FullWidth>
                        <Input
                          placeholder={i18n.t('label.description')}
                          data-size={'medium'}
                          defaultValue={description[i]}
                          onChange={(e) => {
                            const newDescription = [...description];
                            newDescription[i] = e.target.value;
                            setDescription(newDescription);
                          }}
                          error={
                            repairSetUpdateMultipleModal.isSubmitClicked &&
                            !!validators[i].description
                          }
                          helperText={
                            repairSetUpdateMultipleModal.isSubmitClicked
                              ? validators[i].description
                              : ''
                          }
                          rows={2}
                          multiline
                        />
                      </FullWidth>
                    </ModalBodyCustomColumns>
                  </ModalBodyCustomColumns>
                </React.Fragment>
              ))}
              <ModalFooterButtons columns={'max-content 1fr'}>
                <Button
                  size={'medium'}
                  data-color={'primary'}
                  onClick={handleAddRepairClick}
                  justify={'left'}
                >
                  {i18n.t('button.addRepair')}
                </Button>
                <Button
                  size={'medium'}
                  data-color={'destructive'}
                  onClick={handleRemoveLastRepairClick}
                  justify={'left'}
                  disabled={price.length === 1}
                >
                  {i18n.t('button.removeLastRepair')}
                </Button>
                <Button size={'medium'} data-color={'white'} onClick={handleOnClose}>
                  {i18n.t('button.cancel')}
                </Button>
                <Button size={'medium'} data-color={'primary'} onClick={handleSubmitClick}>
                  {i18n.t('button.submit')}
                </Button>
              </ModalFooterButtons>
            </>
          )}
        </ModalWrapper>
      </ModalBase>
      <ImageGallery
        id={'repair-set-update-multiple-modal'}
        photos={[
          ...(oldRepairs?.[zoomImageIndex[0]]?.beforeImages?.map(({ url }) => url) ?? []),
          ...(oldBeforeImages?.[zoomImageIndex[0] - oldRepairs.length]?.map(
            ({ url }: any) => url,
          ) ?? []),
          ...(newBeforeImages?.[zoomImageIndex[0] - oldRepairs.length] ?? []),
        ]}
        startIndex={zoomImageIndex[1]}
      />
    </>
  );
};
